// Branding for surfaces that render BEFORE a tenant is known: the sign-in
// screen and the signed-out guest booking view. Neither can call
// v1-tenant-config — that needs a session — so both read the public widget
// config, which is the one endpoint that answers "who is this business" without
// authentication.

import { requiredEnvValue } from './env'
import { isPublicPurchasePath, isTenantSlugPath } from './tenantPaths'

const supabaseUrl = requiredEnvValue(process.env.EXPO_PUBLIC_SUPABASE_URL)

export interface PublicBranding {
  name: string
  brand_primary_color: string | null
  brand_logo_url: string | null
  brand_tagline: string | null
}

// Written whenever a tenant is resolved (setTenantContext), read on the next
// visit to /login. An operator signing in on their own device is by far the
// common case, and after the first visit this is what makes the sign-in screen
// wear their brand with no URL parameter and no build configuration.
const SLUG_STORAGE_KEY = 'hj.lastTenantSlug'

// A slug is a URL segment, and this value is echoed into a query string and a
// storage key — so refuse anything that is not one rather than trusting what
// came out of storage or off the address bar.
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/i

export function rememberTenantSlug(slug: string | null | undefined): void {
  if (!slug || !SLUG_PATTERN.test(slug)) return
  try {
    localStorage.setItem(SLUG_STORAGE_KEY, slug)
  } catch {
    // Private browsing / storage disabled. Branding is cosmetic — never let it
    // break sign-in.
  }
}

export function readRememberedTenantSlug(): string | null {
  try {
    const value = localStorage.getItem(SLUG_STORAGE_KEY)
    return value && SLUG_PATTERN.test(value) ? value : null
  } catch {
    return null
  }
}

/**
 * Which tenant the sign-in screen should wear, in confidence order. Pure so the
 * precedence is testable without a DOM.
 *
 * 1. `?tenant=` — an explicit statement of intent, and how the guest booking
 *    view's Sign in link arrives. Beats everything.
 * 2. The last tenant this browser resolved. Right for a returning operator.
 * 3. `EXPO_PUBLIC_TENANT_SLUG` — a single-tenant deployment naming its own
 *    tenant, so even a first-ever visit is branded. Absent on multi-tenant
 *    builds, which is why it ranks last rather than being the only source.
 *
 * null means "no tenant known" and the caller shows the platform identity —
 * which is honest, not a failure.
 */
export function resolveSignInTenantSlug(
  search: string,
  remembered: string | null,
  envSlug: string | null,
): string | null {
  const fromQuery = new URLSearchParams(search).get('tenant')
  for (const candidate of [fromQuery, remembered, envSlug]) {
    if (candidate && SLUG_PATTERN.test(candidate)) return candidate
  }
  return null
}

/**
 * Whose install manifest to link before the first render: a tenant's own page
 * (/:slug, /:slug/buy) first, then the sign-in screen's order. An installed app
 * is checked against its manifest on launch, before any tenant has resolved.
 */
export function bootBrandSlug(
  pathname: string,
  search: string,
  remembered: string | null,
  envSlug: string | null,
): string | null {
  if (isTenantSlugPath(pathname) || isPublicPurchasePath(pathname)) {
    const fromPath = pathname.split('/').filter(Boolean)[0]
    if (SLUG_PATTERN.test(fromPath)) return fromPath
  }
  return resolveSignInTenantSlug(search, remembered, envSlug)
}

/** Public, unauthenticated branding read. Resolves null on any failure — every
 *  caller has a fallback, and a branding fetch must never block a sign-in. */
export async function fetchPublicBranding(slug: string): Promise<PublicBranding | null> {
  try {
    const res = await fetch(
      `${supabaseUrl}/functions/v1/v1-widget/config?tenant=${encodeURIComponent(slug)}`,
    )
    if (!res.ok) return null
    const body = await res.json() as { business?: PublicBranding }
    return body.business ?? null
  } catch {
    return null
  }
}
